import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2, SkeletonLoader } from "oolib";
import { UI_BODY_SM, UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Tag } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { SentimentBar } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SentimentBar";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { DatasetCircles } from "@/components/analysisModules/TopicClusteringTreeView/DatasetCircles";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { toneColor, winTone } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { periodFilter } from "./scopeFilters";

/**
 * Signal categories in the period, one row each: how many observations
 * carry a signal of the category, that as a share of every observation in
 * the period (the bar), how many points that share moved since the
 * comparison period, and the datasets those observations come from.
 *
 * The observation is the unit throughout, as in the annotation explorer:
 * an observation carrying signals of two categories is in both rows, so
 * the shares need not sum to 100. Owns its two requests — the category
 * counts for the period and for the comparison period — through the
 * metrics engine's `signalCategory` dimension, with the datasets as a facet
 * of the first.
 *
 *   period, comparePeriod   the shared period selection's
 *   titleByContentType      dataset titles for the source circles
 *   onOpenCategory(value)   a row opens the category's topics (optional)
 */
const DIMENSION = { dimensionType: "signalCategory", facets: { datasets: "meta.contentType" } };
const COMPARE_DIMENSION = { dimensionType: "signalCategory" };

const Table = styled.div`
  display: flex;
  flex-direction: column;
`;
// Every row is its own grid, so every column but the flexible two is a
// fixed width — otherwise a row's circles would set its own column edges.
const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 40px minmax(0, 1fr) 72px 112px;
  column-gap: 16px;
  align-items: center;
  padding: 12px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  &:hover {
    background: ${({ $clickable }) => ($clickable ? colors2.grey2 : "transparent")};
  }
  &:last-of-type {
    border-bottom: none;
  }
`;
const HeaderRow = styled(Row)`
  padding: 12px 8px 8px;
  cursor: default;
  &:hover {
    background: transparent;
  }
`;
const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${({ $color }) => $color};
`;
const Share = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;
const Foot = styled.div`
  padding-top: 8px;
`;

const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}`;
// The colour of a move follows the category's polarity: a negative one
// (dissatisfaction) reads red when rising, a positive one the reverse, a
// neutral one stays grey — a move there is news, not good or bad news. A
// move under half a point is grey whichever way.
const moveColor = (moved, polarity) => {
  if (Math.abs(moved) < 0.5 || polarity === "neutral") return colors2.grey50;
  const tone = moved > 0 ? "up" : "down";
  return toneColor(polarity === "negative" ? tone : winTone(tone));
};

const WidgetSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
    {[0, 1, 2, 3].map((i) => (
      <SkeletonLoader key={i} style={{ height: 40, borderRadius: 8 }} />
    ))}
  </div>
);

export const SignalCategoriesWidget = ({ period, comparePeriod, titleByContentType, onOpenCategory = null }) => {
  const categoryTheme = useSignalCategories();
  const { data: now, isLoading } = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    activeFilters: [periodFilter(period)].filter(Boolean),
    dimensions: [DIMENSION],
    returnMetadata: true,
    queryOptions: { enabled: !!period },
  });
  const { data: then } = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    activeFilters: [periodFilter(comparePeriod)].filter(Boolean),
    dimensions: [COMPARE_DIMENSION],
    returnMetadata: true,
    queryOptions: { enabled: !!comparePeriod },
  });

  // One row per category in the tenant's order, zero when nothing landed.
  const rows = useMemo(() => {
    const byValue = Object.fromEntries((now?.data || []).map((r) => [r.value, r]));
    const thenByValue = Object.fromEntries((then?.data || []).map((r) => [r.value, r]));
    const hasThen = !!comparePeriod && !!then && then.metadata?.totalDocsInScope > 0;
    return categoryTheme.list.map((cat) => {
      const row = byValue[cat.value];
      const share = row?.percentage || 0;
      const shareThen = thenByValue[cat.value]?.percentage || 0;
      return {
        value: cat.value,
        display: cat.display,
        color: categoryTheme.accentOf(cat.value),
        polarity: cat.polarity,
        count: row?.count || 0,
        share,
        moved: hasThen ? share - shareThen : null,
        datasets: row?.datasets || [],
      };
    });
  }, [now, then, comparePeriod, categoryTheme]);

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <Tag size={16} color={colors2.grey80} weight="bold" />
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", minWidth: 0 }}>
            <UI_BODY_SEMIBOLD_SM_DF>Signal categories</UI_BODY_SEMIBOLD_SM_DF>
            <UI_CAPTION style={{ color: colors2.grey60 }}>Share of observations in {period?.display || "this period"}.</UI_CAPTION>
          </div>
        </div>
      }
      style={{ gap: 0 }}
    >
      {isLoading ? (
        <WidgetSkeleton />
      ) : (
        <Table>
          <HeaderRow as="div">
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40 }}>Category</UI_CAPTION_SEMIBOLD_DF>
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40, textAlign: "right" }}>Obs</UI_CAPTION_SEMIBOLD_DF>
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40 }}>Share</UI_CAPTION_SEMIBOLD_DF>
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40, textAlign: "right" }}>Change</UI_CAPTION_SEMIBOLD_DF>
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40 }}>Source</UI_CAPTION_SEMIBOLD_DF>
          </HeaderRow>
          {rows.map((row) => (
            <Row key={row.value} $clickable={!!onOpenCategory} role={onOpenCategory ? "button" : undefined} onClick={onOpenCategory ? () => onOpenCategory(row.value) : undefined}>
              <Name>
                <Dot $color={row.color} />
                <UI_BODY_SM style={{ color: colors2.grey80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.display}</UI_BODY_SM>
              </Name>
              <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80, textAlign: "right" }}>{row.count.toLocaleString()}</UI_BODY_SEMIBOLD_SM_DF>
              <Share>
                <SentimentBar segments={[{ key: row.value, title: row.display, value: Math.min(100, row.share), color: row.color }]} height={8} track roundedEnds />
                <UI_CAPTION_DF style={{ color: colors2.grey60, width: 36, textAlign: "right", flexShrink: 0 }}>{Math.round(row.share)}%</UI_CAPTION_DF>
              </Share>
              <UI_CAPTION_SEMIBOLD_DF style={{ color: row.moved === null ? colors2.grey50 : moveColor(row.moved, row.polarity), textAlign: "right", whiteSpace: "nowrap" }}>
                {row.moved === null ? "–" : `${signed(row.moved)} pts`}
              </UI_CAPTION_SEMIBOLD_DF>
              <DatasetCircles datasets={row.datasets} titleByContentType={titleByContentType} />
            </Row>
          ))}
          <Foot>
            <UI_CAPTION style={{ color: colors2.grey40 }}>
              {comparePeriod ? `pts = share change ${comparePeriod.display} → ${period?.display}` : "pts = share change; no earlier period to compare with"}
            </UI_CAPTION>
          </Foot>
        </Table>
      )}
    </DashboardWidgetShell>
  );
};
